import { useHttp } from "../http/useHttp";
import { useAxiosQuery, UseAxiosQueryOptions } from "../http/useAxiosQuery";
import { useMemo } from "react";
import { useDefaultError } from "@synota-io/synota-shared-ui";
import { paths } from "../generated-types";

type ServiceChargeContractsOperation =
  paths["/dashboard/users/{api_token_uuid}/service-charges/contracts"]["post"];
type UseServiceChargeContractsResponse =
  ServiceChargeContractsOperation["responses"][200]["content"]["application/json"];
type UseServiceChargeContractsRequestBody =
  ServiceChargeContractsOperation["requestBody"]["content"]["application/json"];
type ServiceChargeContractsPath = ServiceChargeContractsOperation["parameters"]["path"];

export type IServiceChargeContract = UseServiceChargeContractsResponse["items"][number];
export type UseServiceChargeContractsInput = UseServiceChargeContractsRequestBody & {
  user_uuid: ServiceChargeContractsPath["api_token_uuid"];
};

export const useServiceChargeContracts = (
  input: UseServiceChargeContractsInput = { user_uuid: "" },
  options: Omit<
    UseAxiosQueryOptions<UseServiceChargeContractsResponse>,
    "queryKey" | "queryFn"
  > = {},
) => {
  const { client } = useHttp();

  const { status, isPending, isFetching, isLoading, data, error, refetch } =
    useAxiosQuery<UseServiceChargeContractsResponse>({
      queryKey: ["user-service-charge-contracts", input],
      queryFn: ({ signal }) => {
        return client.post(`/users/${input.user_uuid}/service-charges/contracts`, input, {
          signal,
        });
      },
      enabled: options.enabled !== false && !!input.user_uuid,
      ...options,
    });

  const [contracts, hasNextPage] = useMemo(() => {
    if (!data) {
      return [[], false];
    }
    return [data.data.items, data.data.has_next_page];
  }, [data]);

  useDefaultError(error);

  return {
    contracts,
    hasNextPage,
    error,
    isLoading,
    isFetching,
    isPending,
    status,
    refetch,
  };
};

export type UseServiceChargeContractsReturn = ReturnType<typeof useServiceChargeContracts>;
